import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    X, ShieldCheck, CheckCircle2, AlertTriangle, XCircle, Info,
    ChevronDown, ChevronRight, FileText, ArrowRight, ExternalLink,
} from 'lucide-react';
import type { FaturaEstruturada, ReconciliacaoResultado, ReconciliacaoCheck } from '../../../types';
import { STATUS_RATEIO_UC_META, type StatusRateioUc } from '../../../src/lib/statusRateioUc';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    arquivoNome?: string;
    estruturado: FaturaEstruturada | null;
    reconciliacao: ReconciliacaoResultado | null;
    pdfUrl?: string | null;
    statusRateio?: StatusRateioUc;
}

const STATUS_META: Record<string, { label: string; ring: string; chip: string }> = {
    ok: { label: 'Confere', ring: 'stroke-emerald-500', chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' },
    revisar: { label: 'Revisar', ring: 'stroke-amber-500', chip: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30' },
    falhou: { label: 'Falhou', ring: 'stroke-red-500', chip: 'bg-red-100 text-red-700 dark:bg-red-900/30' },
};

const iconFor = (tipo: string) => {
    switch (tipo) {
        case 'critico': return <XCircle className="w-4 h-4 text-red-500 shrink-0" />;
        case 'alerta': return <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />;
        case 'info': return <Info className="w-4 h-4 text-sky-500 shrink-0" />;
        default: return <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />;
    }
};
const cardFor = (tipo: string) => {
    switch (tipo) {
        case 'critico': return 'border-l-4 border-l-red-400 bg-red-50/80 dark:bg-red-950/20';
        case 'alerta': return 'border-l-4 border-l-amber-400 bg-amber-50/80 dark:bg-amber-950/20';
        case 'info': return 'border-l-4 border-l-sky-400 bg-sky-50/80 dark:bg-sky-950/20';
        default: return 'border-l-4 border-l-emerald-400 bg-emerald-50/80 dark:bg-emerald-950/20';
    }
};

const Secao: React.FC<{ titulo: string; aberta?: boolean; children: React.ReactNode; contador?: number }> = ({ titulo, aberta = false, children, contador }) => {
    const [open, setOpen] = useState(aberta);
    return (
        <div className="rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
            <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
                    {titulo}
                    {contador !== undefined && <span className="text-[11px] font-medium text-gray-400">({contador})</span>}
                </span>
                {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
            </button>
            {open && <div className="p-4">{children}</div>}
        </div>
    );
};

const Campo: React.FC<{ label: string; valor?: string | number | null }> = ({ label, valor }) => (
    <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{label}</p>
        <p className="text-sm text-gray-800 dark:text-gray-100 truncate" title={String(valor ?? '')}>{valor || '—'}</p>
    </div>
);

const RevisaoFaturaModal: React.FC<Props> = ({ isOpen, onClose, arquivoNome, estruturado, reconciliacao, pdfUrl, statusRateio }) => {
    const status = reconciliacao?.status || 'falhou';
    const score = reconciliacao?.score ?? 0;
    const meta = STATUS_META[status] || STATUS_META.falhou;

    const checks = reconciliacao?.checagens || [];
    const criticos = checks.filter(c => c.tipo === 'critico');
    const alertas = checks.filter(c => c.tipo === 'alerta');
    const oks = checks.filter(c => c.tipo === 'ok' || c.tipo === 'info');
    const ordenadas = useMemo(() => [...criticos, ...alertas, ...oks], [checks]);

    const cab = estruturado?.cabecalho || {};
    const itens = estruturado?.itens || [];
    const scee = estruturado?.scee || [];
    const historico = estruturado?.historico || [];
    const tributos = estruturado?.tributos || [];

    const circ = 2 * Math.PI * 26;
    const dash = circ - (score / 100) * circ;

    if (!isOpen) return null;

    const renderCheck = (c: ReconciliacaoCheck, i: number) => (
        <div key={i} className={`rounded-xl p-3 ${cardFor(c.tipo)}`}>
            <div className="flex items-start gap-2.5">
                {iconFor(c.tipo)}
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{c.categoria}</span>
                        <span className="text-gray-300">·</span>
                        <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 truncate">{c.campo}</span>
                    </div>
                    <p className="text-[13px] text-gray-600 dark:text-gray-300 leading-relaxed">{c.mensagem}</p>
                    {c.valor_declarado && c.valor_esperado && (
                        <div className="mt-2 flex items-center gap-2 text-[11px] flex-wrap">
                            <span className="inline-flex items-center gap-1.5 bg-white/70 dark:bg-black/20 rounded-lg px-2.5 py-1 border border-gray-200/50 dark:border-gray-700/50">
                                <span className="text-gray-400 font-medium">Declarado</span>
                                <span className="font-mono font-bold text-gray-700 dark:text-gray-200">{c.valor_declarado}</span>
                            </span>
                            <ArrowRight className="w-3 h-3 text-gray-300" />
                            <span className="inline-flex items-center gap-1.5 bg-white/70 dark:bg-black/20 rounded-lg px-2.5 py-1 border border-gray-200/50 dark:border-gray-700/50">
                                <span className="text-gray-400 font-medium">Esperado</span>
                                <span className="font-mono font-bold text-gray-700 dark:text-gray-200">{c.valor_esperado}</span>
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="relative px-6 py-4 bg-gradient-to-br from-emerald-600 via-green-600 to-teal-700">
                    <div className="relative flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 bg-white/15 rounded-2xl flex items-center justify-center border border-white/20 shrink-0">
                                <ShieldCheck className="w-5 h-5 text-white" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-base font-bold text-white truncate">{cab.nome_cliente || arquivoNome || 'Fatura'}</h2>
                                <p className="text-emerald-100/80 text-xs mt-0.5 truncate">
                                    UC {cab.unidade_consumidora || '—'} · {cab.conta_mes || '—'} · Grupo {cab.grupo || '—'}
                                </p>
                            </div>
                        </div>
                        {/* Score ring */}
                        <div className="relative shrink-0">
                            <svg width="58" height="58" viewBox="0 0 58 58" className="-rotate-90">
                                <circle cx="29" cy="29" r="26" fill="none" className="stroke-white/20" strokeWidth="4" />
                                <circle cx="29" cy="29" r="26" fill="none" className="stroke-white" strokeWidth="4" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={dash} />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-sm font-bold text-white">{score}</span>
                            </div>
                        </div>
                        <button onClick={onClose} className="w-8 h-8 bg-white/15 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/25 transition-all shrink-0">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="relative mt-3 flex items-center gap-2 flex-wrap">
                        <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${meta.chip}`}>{meta.label}</span>
                        {statusRateio && (() => {
                            const rateioMeta = STATUS_RATEIO_UC_META[statusRateio];
                            return (
                                <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full border ${rateioMeta.className}`}>
                                    {rateioMeta.icon} {rateioMeta.label}
                                </span>
                            );
                        })()}
                        {criticos.length > 0 && <span className="text-[11px] text-white/90">{criticos.length} crítico(s)</span>}
                        {alertas.length > 0 && <span className="text-[11px] text-white/90">· {alertas.length} alerta(s)</span>}
                        <span className="text-[11px] text-white/70">· R$ {cab.total_pagar || '—'}</span>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 custom-scrollbar">
                    {/* Reconciliação */}
                    <div>
                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Auditoria ({checks.length})</h3>
                        <div className="space-y-2">{ordenadas.map(renderCheck)}</div>
                    </div>

                    {/* Cabeçalho */}
                    <Secao titulo="Cabeçalho" aberta>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <Campo label="UC" valor={cab.unidade_consumidora} />
                            <Campo label="Cliente" valor={cab.nome_cliente} />
                            <Campo label="CPF/CNPJ" valor={cab.cpf_cnpj} />
                            <Campo label="Mês/Ref" valor={cab.conta_mes} />
                            <Campo label="Vencimento" valor={cab.vencimento} />
                            <Campo label="Total" valor={cab.total_pagar} />
                            <Campo label="Grupo/Sub" valor={`${cab.grupo || ''} ${cab.sub_grupo || ''}`.trim()} />
                            <Campo label="Fornecimento" valor={cab.tipo_fornecimento} />
                            <Campo label="Consumo (kWh)" valor={cab.consumo_kwh} />
                        </div>
                    </Secao>

                    {/* Itens */}
                    <Secao titulo="Itens da fatura" aberta contador={itens.length}>
                        <div className="overflow-x-auto -mx-1">
                            <table className="w-full text-[12px]">
                                <thead>
                                    <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-800">
                                        <th className="py-1.5 pr-2 font-medium">Descrição</th>
                                        <th className="py-1.5 px-2 font-medium">Posto</th>
                                        <th className="py-1.5 px-2 font-medium text-right">Qtd</th>
                                        <th className="py-1.5 px-2 font-medium text-right">Tarifa</th>
                                        <th className="py-1.5 px-2 font-medium text-right">Preço c/ trib.</th>
                                        <th className="py-1.5 pl-2 font-medium text-right">Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {itens.map((it, i) => (
                                        <tr key={i} className="border-b border-gray-50 dark:border-gray-800/50">
                                            <td className="py-1.5 pr-2 text-gray-700 dark:text-gray-200">
                                                {it.descricao}
                                                {it.tipo_gd && <span className="ml-1 text-[10px] text-emerald-600">{it.tipo_gd}</span>}
                                            </td>
                                            <td className="py-1.5 px-2 text-gray-500">{it.posto || ''}</td>
                                            <td className="py-1.5 px-2 text-right font-mono text-gray-600 dark:text-gray-300">{it.qtd}</td>
                                            <td className="py-1.5 px-2 text-right font-mono text-gray-500">{it.tarifa_unit}</td>
                                            <td className="py-1.5 px-2 text-right font-mono text-gray-500">{it.preco_unit_com_tributos}</td>
                                            <td className="py-1.5 pl-2 text-right font-mono font-semibold text-gray-800 dark:text-gray-100">{it.valor}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Secao>

                    {scee.length > 0 && (
                        <Secao titulo="SCEE / Geração distribuída" contador={scee.length}>
                            <div className="space-y-2">
                                {scee.map((s, i) => (
                                    <div key={i} className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px] p-2 rounded-lg bg-gray-50 dark:bg-gray-800/40">
                                        <Campo label="UC Geradora" valor={s.uc_geradora} />
                                        <Campo label="Geração (kWh)" valor={s.geracao_ciclo_kwh} />
                                        <Campo label="Excedente (kWh)" valor={s.excedente_recebido_kwh} />
                                        <Campo label="Rateio" valor={s.percent_rateio} />
                                    </div>
                                ))}
                            </div>
                        </Secao>
                    )}

                    {tributos.length > 0 && (
                        <Secao titulo="Tributos" contador={tributos.length}>
                            <div className="grid grid-cols-3 gap-3">
                                {tributos.map((t, i) => (
                                    <div key={i} className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800/40">
                                        <p className="text-[11px] font-bold text-gray-600 dark:text-gray-300">{t.tipo}</p>
                                        <p className="text-[11px] text-gray-500">Base {t.base} · {t.aliquota}</p>
                                        <p className="text-[12px] font-mono font-semibold">{t.valor}</p>
                                    </div>
                                ))}
                            </div>
                        </Secao>
                    )}

                    {historico.length > 0 && (
                        <Secao titulo="Histórico de consumo" contador={historico.length}>
                            <div className="flex flex-wrap gap-1.5">
                                {historico.map((h, i) => (
                                    <span key={i} className="text-[11px] font-mono px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                                        {h.mes_ano}: {h.consumo_faturado}
                                    </span>
                                ))}
                            </div>
                        </Secao>
                    )}

                    {estruturado?.meta?.avisos && estruturado.meta.avisos.length > 0 && (
                        <div className="text-[11px] text-amber-600 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2">
                            Avisos do parser: {estruturado.meta.avisos.join(' · ')}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center justify-between">
                    {pdfUrl ? (
                        <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-[12px] font-medium text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1.5">
                            <FileText className="w-4 h-4" /> Ver PDF original <ExternalLink className="w-3 h-3" />
                        </a>
                    ) : <span className="text-[11px] text-gray-400">Parser v{estruturado?.meta?.parser_version || '—'} · OCR {Math.round((estruturado?.meta?.ocr_confidence || 0) * 100)}%</span>}
                    <button onClick={onClose} className="px-5 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl hover:from-emerald-600 hover:to-green-700 transition-all">Fechar</button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default RevisaoFaturaModal;
